import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheckCircle, faCircle } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { getTranslation } from "../../translations/getTranslations";
import { forgotPassword, validateResetPassword, resetPassword } from "../../services/userService";
import { useNotification } from "../../notifications/Notifications";
import { setForgotPasswordStep, setVerificationCodeTimestamp } from "../../redux/slices/authSlice";

const ForgotPasswordModal = ({ showModal, onClose }) => {
  const dispatch = useDispatch();
  const notify = useNotification();

  const verificationCodeTimestamp = useSelector(state => state.auth.user.verificationCodeTimestamp);
  const forgotPasswordStep = useSelector(state => state.auth.user.forgotPasswordStep);
  const language = useSelector(state => state.settings.language);

  const [resetEmail, setResetEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [step, setStep] = useState(1);
  const [minutesLeft, setMinutesLeft] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const [validationStatus, setValidationStatus] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumeric: false,
    hasSpecialChar: false,
    isMinLength: false,
  });

  const translation = key => getTranslation("forgotPasswordModal", key, language);

  useEffect(() => {
    setErrorMessage("");
    // setResetEmail("");
    setVerificationCode("");
    setNewPassword("");
    setConfirmPassword("");
    decideStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    if (verificationCodeTimestamp <= 0) {
      return;
    }

    const updateCountdown = () => {
      const currentTime = Date.now();
      const difference = currentTime - verificationCodeTimestamp;

      if (difference >= 15 * 60 * 1000) {
        setMinutesLeft(0);
        setSecondsLeft(0);
        dispatch(setVerificationCodeTimestamp(0));
        dispatch(setForgotPasswordStep(1));

        setErrorMessage("");
        setResetEmail("");
        setVerificationCode("");
        setNewPassword("");
        setConfirmPassword("");
        setStep(1);

        return;
      }

      const remainingSeconds = Math.max(0, (15 * 60 * 1000 - difference) / 1000);

      setMinutesLeft(Math.floor(remainingSeconds / 60));
      setSecondsLeft(Math.floor(remainingSeconds % 60));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCodeTimestamp]);

  const decideStep = () => {
    if (forgotPasswordStep === 2) {
      setStep(2);
    } else if (forgotPasswordStep === 3) {
      setStep(3);
    } else {
      setStep(1);
      setMinutesLeft(0);
      setSecondsLeft(0);
      dispatch(setVerificationCodeTimestamp(0));
      dispatch(setForgotPasswordStep(1));
    }
  };

  const checkPasswordStrength = password => {
    setValidationStatus({
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumeric: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      isMinLength: password.length >= 8,
    });
  };

  const handleNewPasswordChange = e => {
    const password = e.target.value;
    setNewPassword(password);
    checkPasswordStrength(password);
  };

  const handleForgotPasswordSubmit = async () => {
    if (!resetEmail) {
      setErrorMessage(translation("_requiredEmail"));
      return;
    }

    try {
      const response = await forgotPassword(resetEmail);
      const successMessage = response.trim().replace(/^"+|"+$/g, "");
      notify("success", translation(successMessage));
      // notify("success", translation("_resetLinkSent"));

      dispatch(setVerificationCodeTimestamp(Date.now()));
      dispatch(setForgotPasswordStep(2));
      setStep(2);
    } catch (error) {
      const errorMessage = error.message.trim().replace(/^"+|"+$/g, "");
      setErrorMessage(translation(errorMessage));
      // setErrorMessage(translation("_resetLinkError"));
    }
  };

  const handleVerificationCodeSubmit = async () => {
    if (!verificationCode) {
      setErrorMessage(translation("_requiredCode"));
      return;
    }

    try {
      await validateResetPassword(resetEmail, verificationCode);

      dispatch(setVerificationCodeTimestamp(0));
      dispatch(setForgotPasswordStep(3));
      setStep(3);
      setErrorMessage("");
    } catch (error) {
      setVerificationCode("");
      setErrorMessage(translation("_invalidCode"));
    }
  };

  const handlePasswordResetSubmit = async () => {
    if (!newPassword || !confirmPassword) {
      setErrorMessage(translation("_requiredPasswords"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage(translation("_passwordsMismatch"));
      return;
    }

    try {
      await resetPassword(resetEmail, newPassword, confirmPassword);
      notify("success", translation("_passwordUpdated"));
      onClose();

      dispatch(setVerificationCodeTimestamp(0));
      dispatch(setForgotPasswordStep(1));
    } catch (error) {
      setVerificationCode("");
      setErrorMessage(translation("_passwordUpdateError"));
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div
        className={`relative w-[500px] rounded bg-backgroundPrimary p-6 text-center text-lg text-textPrimary shadow-lg`}
      >
        <button
          className={`absolute right-1 top-0 p-2 text-xl text-buttonCancel hover:text-buttonCancelHover`}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="mb-6 text-2xl font-semibold">{translation("_forgotPasswordModalTitle")}</h2>
        {step === 1 && (
          <>
            <p className="mb-4 text-base">{translation("_forgotPasswordDesc")}</p>
            <input
              type="email"
              className={`w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_emailPlaceholder")}
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
            />
            <p className="my-2 h-5 text-sm text-red-500">{errorMessage}</p>

            <button
              className={`w-full cursor-pointer rounded bg-buttonSubmit p-3 text-base text-buttonText hover:bg-buttonSubmitHover`}
              onClick={handleForgotPasswordSubmit}
            >
              {translation("_sendResetLink")}
            </button>
          </>
        )}
        {step === 2 && (
          <>
            <p className="mb-4 text-base">{translation("_verificationPasswordDesc")}</p>

            <input
              type="email"
              className={`mb-3 w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              value={resetEmail}
              disabled={true}
            />
            <input
              type="text"
              className={`mb-3 w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_verificationCodePlaceholder")}
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
            />
            <p className="my-2 h-5 text-sm text-red-500">{errorMessage}</p>
            <p className="my-2 h-5 text-sm">
              {translation("_timeLeft")}: {minutesLeft.toString().padStart(2, 0)}:
              {secondsLeft.toString().padStart(2, 0)}
            </p>

            <button
              className={`w-full cursor-pointer rounded bg-buttonSubmit p-3 text-base text-buttonText hover:bg-buttonSubmitHover`}
              onClick={handleVerificationCodeSubmit}
            >
              {translation("_verifyCode")}
            </button>
          </>
        )}
        {step === 3 && (
          <>
            <p className="mb-4 text-base">{translation("_resetPasswordDesc")}</p>
            <input
              type="password"
              className={`w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_newPasswordPlaceholder")}
              value={newPassword}
              onChange={handleNewPasswordChange}
            />
            <input
              type="password"
              className={`mt-2 w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_confirmPasswordPlaceholder")}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
            <div className="mt-4 flex justify-center">
              <ul className="w-[90%] space-y-1 text-left text-xs">
                <li
                  className={`flex items-center ${validationStatus.hasLower ? "text-green-900" : "text-textPrimary"}`}
                >
                  {translation("_passwordValidationDesc")}
                </li>
                <li
                  className={`flex items-center ${validationStatus.hasLower ? "text-green-900" : "text-textPrimary"}`}
                >
                  <FontAwesomeIcon icon={validationStatus.hasLower ? faCheckCircle : faCircle} className="mr-2" />
                  {translation("_passwordValidationLowLetter")}
                </li>
                <li
                  className={`flex items-center ${validationStatus.hasUpper ? "text-green-900" : "text-textPrimary"}`}
                >
                  <FontAwesomeIcon icon={validationStatus.hasUpper ? faCheckCircle : faCircle} className="mr-2" />
                  {translation("_passwordValidationUpperLetter")}
                </li>
                <li
                  className={`flex items-center ${validationStatus.hasNumeric ? "text-green-900" : "text-textPrimary"}`}
                >
                  <FontAwesomeIcon icon={validationStatus.hasNumeric ? faCheckCircle : faCircle} className="mr-2" />
                  {translation("_passwordValidationNumeric")}
                </li>
                <li
                  className={`flex items-center ${validationStatus.hasSpecialChar ? "text-green-900" : "text-textPrimary"}`}
                >
                  <FontAwesomeIcon icon={validationStatus.hasSpecialChar ? faCheckCircle : faCircle} className="mr-2" />
                  {translation("_passwordValidationSpecialChar")}
                </li>
                <li
                  className={`flex items-center ${validationStatus.isMinLength ? "text-green-900" : "text-textPrimary"}`}
                >
                  <FontAwesomeIcon icon={validationStatus.isMinLength ? faCheckCircle : faCircle} className="mr-2" />
                  {translation("_passwordValidationLength")}
                </li>
              </ul>
            </div>
            <p className="my-2 h-5 text-sm text-red-500">{errorMessage}</p>

            <button
              className={`w-full cursor-pointer rounded bg-buttonSubmit p-3 text-base text-buttonText hover:bg-buttonSubmitHover`}
              onClick={handlePasswordResetSubmit}
            >
              {translation("_submit")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
